// Local type extensions for the web app
// Shared types imported from @arcanum-hq/shared-types
// App-specific type extensions go here

import type { ConversationItem } from '@/hooks/use-conversations-v2';
import {
  type PipelineStatus,
  PIPELINE_STATUS_COLOR_TOKEN,
} from '@arcanum-hq/shared-types';
import {
  formatCompactCurrency,
  formatMultiple,
  formatPercent,
} from '@/lib/format';

/**
 * Pipeline metric formatters — single source of truth for cockpit + pipeline
 * tables. The server's `displayVersion.{irr,dscr,units,basis}` projection
 * (`apps/api/src/modules/conversations/conversations.repository.ts`) returns
 * raw `String(numericValue)` for IRR/DSCR off `metrics[]` (e.g. "0.2251815…",
 * "1.2853076…") and may pass through pre-formatted strings for units/basis
 * captured at create-time via dealMetadata. These helpers normalize both
 * shapes — numeric coercion takes the formatting path, non-numeric strings
 * pass through unchanged so legacy creator-intent values like "$45M" / "250"
 * keep their existing display.
 */

function formatIrr(raw: string | null | undefined): string | undefined {
  if (raw == null || raw === '') return undefined;
  // Already formatted (carries % or x or letters) — pass through.
  if (/[%a-zA-Z]/.test(raw)) return raw;
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  // IRR is conventionally a fraction (0–1.5 range covers 0%-150%). If a model
  // ever returns a basis-point-style integer (≥10), treat as already-percent.
  if (Math.abs(n) >= 10) return `${n.toFixed(1)}%`;
  return formatPercent(n);
}

function formatDscr(raw: string | null | undefined): string | undefined {
  if (raw == null || raw === '') return undefined;
  if (/[xX]/.test(raw)) return raw;
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return formatMultiple(n);
}

function formatUnits(
  raw: number | string | null | undefined
): number | undefined {
  if (raw == null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n);
}

function formatBasis(raw: string | null | undefined): string | undefined {
  if (raw == null || raw === '') return undefined;
  // Already formatted (`$45M`, `$1.2B`, etc.) — pass through.
  if (/[$KMB%]/.test(raw)) return raw;
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return formatCompactCurrency(n);
}

/**
 * UI deal representation used by Cockpit and Pipeline pages.
 * Maps from ConversationItem with optional financial fields.
 *
 * conversation-model-versioning Phase 5 Session 5 (2026-05-17):
 * - Added `dscr?: string` for the canonical 7-column pipeline surface.
 * - `yoc` kept for back-compat with DealPinMap/solver consumers (still read
 *   from ConversationItem.yoc — the legacy dealMetadata projection).
 */
export interface Deal {
  id: string;
  name: string;
  status: PipelineStatus;
  modelId: string;
  createdAt: string;
  updatedAt: string;
  // Optional fields — not available from list endpoint
  market?: string;
  units?: number;
  basis?: string;
  irr?: string;
  dscr?: string;
  yoc?: string;
  lat?: number;
  lng?: number;
}

/**
 * Map a ConversationItem from the list endpoint to a Deal.
 *
 * conversation-model-versioning Phase 5 Session 5 (2026-05-17):
 * - `status` now reads `c.pipelineStatus` directly (fallback 'Analyzing').
 *   The orphaned `mapStatus()` transitional stub has been deleted — it existed
 *   only because Session 7 ran before Session 5 in the DAG and could not
 *   remove the call site while this function was live.
 * - Version-gated fields: when `c.displayVersion` is present (hasVersion true),
 *   irr/dscr/market/units/basis read from the display-resolved frozen snapshot
 *   (ADR 0018 server projection). Legacy conversions fall back to the live
 *   dealMetadata fields (market/units/basis/irr from ConversationItem direct
 *   fields) for non-version-gated conversations (Builder sidebar, Solver, etc.).
 */
export function conversationToDeal(c: ConversationItem): Deal {
  const dv = c.displayVersion;

  // Geocoded coords (cockpit-lovable-alignment /fix 2026-05-15).
  // JSONB `->>` delivers text; Number(null/'')===0 and Number('abc')===NaN
  // are both rejected so a missing/garbage coord degrades to undefined
  // (→ pseudoPosition), never 0,0 (Gulf-of-Guinea null-island).
  const latCoerced = c.lat != null ? Number(c.lat) : undefined;
  const lngCoerced = c.lng != null ? Number(c.lng) : undefined;

  return {
    id: c.id,
    name: c.title,
    // Session 5: read c.pipelineStatus directly; fallback 'Analyzing'.
    // mapStatus() stub deleted — its only call site was here.
    status: c.pipelineStatus ?? 'Analyzing',
    modelId: c.modelId,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    // Priority: displayVersion fields (frozen snapshot) → live dealMetadata.
    // Formatter helpers normalize numeric strings off `metrics[]`
    // (e.g. "0.225…" → "22.5%") AND pass through legacy creator-intent
    // strings ("$45M", "250") unchanged.
    market: dv?.market ?? c.market ?? undefined,
    units: formatUnits(dv?.units ?? c.units),
    basis: formatBasis(dv?.basis ?? c.basis),
    irr: formatIrr(dv?.irr ?? c.irr),
    dscr: formatDscr(dv?.dscr),
    yoc: c.yoc ?? undefined,
    lat: Number.isFinite(latCoerced) ? latCoerced : undefined,
    lng: Number.isFinite(lngCoerced) ? lngCoerced : undefined,
  };
}

/**
 * Status colour map — CSS `var()` form for plain-colour sites
 * (fill=, color:, stroke= — single-token references resolve correctly).
 *
 * For alpha-concat sites (border `${hex}33` / glow `${hex}88`) use
 * PIPELINE_STATUS_HEX (exported below) — CSS does NOT compose `var(--X)NN`.
 *
 * conversation-model-versioning Phase 5 Session 7 (2026-05-17):
 * replaces the legacy 4-value deal-status map with this 6-value set.
 */
export const PIPELINE_STATUS_COLORS: Record<PipelineStatus, string> = {
  Analyzing: 'var(--color-status-analyzing)',
  'In Market': 'var(--color-status-in-market)',
  'LOI Submitted': 'var(--color-status-loi-submitted)',
  'Under Contract': 'var(--color-status-under-contract)',
  Closed: 'var(--color-status-closed)',
  Passed: 'var(--color-status-passed)',
};

/**
 * Hex-resolved counterpart to `PIPELINE_STATUS_COLORS` for inline-style
 * alpha concatenation (e.g. `${hex}33` border, `${hex}88` shadow). CSS does
 * NOT compose `var(--X)NN` — `'var(--color-status-analyzing)33'` reaches the
 * browser as a literal string and parses as invalid.
 *
 * Lifted from `DealPinMap.tsx` to `types/index.ts` (2026-05-18) so
 * `<StatusDropdown>` can import without creating a viz→pipeline cycle.
 * `DealPinMap.tsx` imports from here instead of defining its own local const.
 *
 * Values match `apps/web/src/index.css` `@theme` design tokens.
 */
export const PIPELINE_STATUS_HEX: Record<PipelineStatus, string> = {
  Analyzing: '#d4960f', // --color-status-analyzing
  'In Market': '#22a899', // --color-status-in-market
  'LOI Submitted': '#3a78b8', // --color-status-loi-submitted
  'Under Contract': '#7860a8', // --color-status-under-contract
  Closed: '#10d67a', // --color-status-closed
  Passed: '#586878', // --color-status-passed
};

// Re-export for consumer convenience — avoids a double-import of shared-types
// at every pipeline consumer that also needs the order array.
export type { PipelineStatus };
export { PIPELINE_STATUS_COLOR_TOKEN };
